import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import { fetchProductDetailByIdInClient, postProductArrivalNotices } from 'src/libs/api/productsApi';
import type { Product } from 'src/models/product/schema';
import { LocalStorageCache } from 'src/services/cache/LocalStorageCache';
import { AuthContext } from 'src/stores/Auth';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';
import { useModal } from './useModal';

const OLD_NOTICE_RESTOCK_KEY = 'backordered';

const backordered: LocalStorageCache<'backordered', number[]> = new LocalStorageCache(OLD_NOTICE_RESTOCK_KEY, []);

const subscribeExistingId = async (productId: number, locale?: string) => {
  if (Array.isArray(backordered.get()) && locale) {
    const uniqueBackordered = Array.from(new Set(backordered.get()));
    if (uniqueBackordered.every((id) => id !== productId)) return;
    await postProductArrivalNotices(productId, locale);
    backordered.set(uniqueBackordered.filter((id) => id !== productId));
  }
};

export const useNoticeReStock = (product: Product) => {
  const { productId, noticeDate } = product;
  const { locale } = useRouter();
  const { user } = useContext(AuthContext);
  const { setIsVisible, setModalParams } = useModal('noticeModal');
  const { setIsVisible: setIsRestockRegisterModalVisible, setModalParams: setIsRestockRegisterModalParams } =
    useModal('reStockRegisterModal');
  const { setIsVisible: setIsLoginModalVisible, setModalParams: setLoginModalParams } = useModal('loginModal');
  const localeResources = useContext(I18nContext);

  useEffect(() => {
    subscribeExistingId(productId, locale);
  }, [locale, productId]);

  const showReStockRegisterModal = async () => {
    if (!user) {
      setLoginModalParams({
        title: localeResources.products.stockNotificationNotLogin
      });
      setIsLoginModalVisible(true);
      return;
    }

    let shouldNotifyArrival = false;
    const res = await fetchProductDetailByIdInClient(productId);
    if (res.isRight()) {
      shouldNotifyArrival = !!res.value.shouldNotifyArrival;
    }

    setIsRestockRegisterModalParams({
      type: 'alert',
      function: () => onRestockRegister(),
      extraData: { noticeDate, shouldNotifyArrival }
    });
    setIsRestockRegisterModalVisible(true);
    mixpanel.showReStockRegisterModal(productId);
  };

  // 入荷連絡ボタン関数
  const onRestockRegister = async () => {
    if (!locale) return;
    setIsRestockRegisterModalVisible(false);

    const response = await postProductArrivalNotices(productId, locale);

    if (response.isLeft()) {
      setModalParams({
        type: 'warning',
        title: localeResources.commons.errorOccurred,
        text: localeResources.commons.tryAgain
      });
    } else {
      setModalParams({
        type: 'info',
        title: localeResources.products.notice.restockNotificationSetup
      });
      mixpanel.onBackordered(productId, user ? user.id : '');
    }
    setIsVisible(true);
  };

  return {
    onRestockRegister,
    showReStockRegisterModal
  };
};
